import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { SmilePlus } from 'lucide-react';
import {
  ChatMessageReaction,
  EMOJIS_DE_REACAO,
  resumirReacoes,
  alternarMinhaReacao,
  descreverQuemReagiu,
} from './reactions';
import { PosicaoDoSeletor, posicionarSeletor } from './reactionPicker';
import './MessageReactions.css';

// A feature aparece em dois lugares distintos de propósito: as pílulas com a
// contagem ficam sob a mensagem, junto do conteúdo a que se referem, e o botão
// de reagir vai para a fileira de ações (responder, criar tarefa, mais opções),
// que é onde se procura o que fazer com uma mensagem.

interface ReacoesProps {
  reactions: ChatMessageReaction[];
  meuAuthId: string | null;
  onChange: (novasReacoes: ChatMessageReaction[], emoji: string, emojiAtual: string | null) => void;
}

function useReagir({ reactions, meuAuthId, onChange }: ReacoesProps) {
  const meuEmoji = useMemo(
    () => reactions.find(r => r.user_auth_id === meuAuthId)?.emoji ?? null,
    [reactions, meuAuthId]
  );

  const reagir = (emoji: string) => {
    if (!meuAuthId) return;
    // A contagem muda na hora; quem chama cuida de persistir e de desfazer se
    // o servidor recusar.
    onChange(alternarMinhaReacao(reactions, meuAuthId, emoji), emoji, meuEmoji);
  };

  return { meuEmoji, reagir };
}

// Pílulas com a contagem, sob a mensagem. Não ocupam espaço quando ninguém
// reagiu — uma linha vazia embaixo de cada mensagem afastaria a conversa.
export const ReactionPills: React.FC<ReacoesProps & { nomePorAuthId?: Record<string, string> }> = props => {
  const { reactions, meuAuthId, nomePorAuthId = {} } = props;
  const { reagir } = useReagir(props);

  const resumo = useMemo(() => resumirReacoes(reactions, meuAuthId), [reactions, meuAuthId]);

  if (resumo.length === 0) return null;

  return (
    <div className="message-reactions">
      {resumo.map(({ emoji, total, souEu }) => {
        const quemReagiu = descreverQuemReagiu(reactions, emoji, meuAuthId, nomePorAuthId);
        return (
        <button
          key={emoji}
          type="button"
          className={`reaction-pill ${souEu ? 'mine' : ''}`}
          onClick={() => reagir(emoji)}
          // O tooltip diz quem reagiu; a ação do clique vai junto, na segunda linha.
          title={`${quemReagiu}\n${souEu ? 'Clique para remover a sua' : 'Clique para reagir também'}`}
          aria-label={quemReagiu}
          aria-pressed={souEu}
        >
          <span aria-hidden="true">{emoji}</span>
          <span className="reaction-count">{total}</span>
        </button>
        );
      })}
    </div>
  );
};

// Botão de reagir: mais uma bolinha na fileira de ações da mensagem, com o
// mesmo desenho das outras.
export const ReactionButton: React.FC<ReacoesProps> = props => {
  const [posicao, setPosicao] = useState<PosicaoDoSeletor | null>(null);
  const botaoRef = useRef<HTMLButtonElement>(null);
  const seletorRef = useRef<HTMLDivElement>(null);
  const { meuEmoji, reagir } = useReagir(props);
  const seletorAberto = posicao !== null;

  const fechar = useCallback(() => setPosicao(null), []);

  const calcular = useCallback((tamanho?: { largura: number; altura: number }) => {
    const botao = botaoRef.current;
    if (!botao) return;
    setPosicao(
      posicionarSeletor(
        botao.getBoundingClientRect(),
        { width: window.innerWidth, height: window.innerHeight },
        tamanho
      )
    );
  }, []);

  // A primeira posição sai da medida estimada em TAMANHO_DO_SELETOR, porque o
  // seletor ainda não existe. Assim que ele existe, refaz a conta com a largura
  // real — antes de pintar, então não há pulo visível.
  useLayoutEffect(() => {
    const seletor = seletorRef.current;
    if (!seletor || !posicao) return;

    const largura = seletor.offsetWidth;
    const altura = seletor.offsetHeight;
    const corrigida = posicionarSeletor(
      botaoRef.current!.getBoundingClientRect(),
      { width: window.innerWidth, height: window.innerHeight },
      { largura, altura }
    );

    if (corrigida.left !== posicao.left || corrigida.top !== posicao.top) {
      setPosicao(corrigida);
    }
  }, [posicao]);

  // O seletor vive fora da mensagem e não acompanha o scroll: em vez de deixá-lo
  // flutuando longe do botão, fecha. Também fecha no Esc, como todo menu.
  useEffect(() => {
    if (!seletorAberto) return;

    const aoTeclar = (evento: KeyboardEvent) => {
      if (evento.key === 'Escape') fechar();
    };

    window.addEventListener('scroll', fechar, true);
    window.addEventListener('resize', fechar);
    window.addEventListener('keydown', aoTeclar);

    return () => {
      window.removeEventListener('scroll', fechar, true);
      window.removeEventListener('resize', fechar);
      window.removeEventListener('keydown', aoTeclar);
    };
  }, [seletorAberto, fechar]);

  const escolher = (emoji: string) => {
    fechar();
    reagir(emoji);
  };

  return (
    <div className="reaction-picker-anchor">
      <button
        ref={botaoRef}
        type="button"
        onClick={() => (seletorAberto ? fechar() : calcular())}
        className="text-gray-400 hover:text-gray-600 bg-white rounded-full p-0.5 shadow-sm hover:shadow"
        title="Reagir"
        aria-label="Reagir à mensagem"
        aria-expanded={seletorAberto}
      >
        <SmilePlus className="w-3.5 h-3.5" />
      </button>

      {/* Na raiz da página, e não ao lado do botão: dentro da conversa o seletor
          era recortado pelo overflow da lista e do painel. */}
      {posicao &&
        createPortal(
          <>
            {/* Camada que fecha o seletor ao clicar em qualquer outro lugar. */}
            <div className="reaction-picker-backdrop" onClick={fechar} />
            <div
              ref={seletorRef}
              className="reaction-picker"
              role="menu"
              style={{ left: posicao.left, top: posicao.top }}
            >
              {EMOJIS_DE_REACAO.map(emoji => (
                <button
                  key={emoji}
                  type="button"
                  role="menuitem"
                  className={`reaction-option ${meuEmoji === emoji ? 'mine' : ''}`}
                  onClick={() => escolher(emoji)}
                  title={emoji}
                >
                  {emoji}
                </button>
              ))}
            </div>
          </>,
          document.body
        )}
    </div>
  );
};
